import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Nav,
  Tab,
  Button,
  Form,
  Table,
  Modal,
  InputGroup,
  Spinner,
  Alert
} from 'react-bootstrap';
import {
  Search,
  Plus,
  Download,
  Edit2,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Database,
  FileText,
  Save,
  X,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { configs } from '../../config';
import './RAGManagement.css';

const RAG_BASE_URL = configs.rag_base_url;

const INDICES = [
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'question_sql', label: 'Question SQL', icon: Database },
  { key: 'ddl', label: 'DDL', icon: Database },
  { key: 'sectors_brands', label: 'Sectors & Brands', icon: Database, hasSubTabs: true },
  { key: 'fewshot', label: 'Few Shot', icon: Database }
];

const RAGManagement = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState('documents');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [newDocument, setNewDocument] = useState({});
  const [healthStatus, setHealthStatus] = useState({
    cmoBackend: 'checking',
    intelligenceLayer: 'checking',
    ragSystem: 'checking'
  });
  const [totalDocs, setTotalDocs] = useState(0);
  const [alert, setAlert] = useState(null);
  const [sectorsBrandsTab, setSectorsBrandsTab] = useState('all'); // 'all', 'brands', 'categories'
  const [searchInput, setSearchInput] = useState(''); // Separate input state from actual search query

  // Health check function
  const checkHealth = async () => {
    // CMO Backend Health Check
    try {
      await axios.get('https://cmo-app.cmoverse.ai/sampler/health');
      setHealthStatus(prev => ({ ...prev, cmoBackend: 'healthy' }));
    } catch (error) {
      setHealthStatus(prev => ({ ...prev, cmoBackend: 'unhealthy' }));
    }

    // Intelligence Layer Health Check (skip in development)
    if (process.env.NODE_ENV !== 'development') {
      try {
        await axios.get('http://cmo-chat-with-data-proxy:8080/healthz');
        setHealthStatus(prev => ({ ...prev, intelligenceLayer: 'healthy' }));
      } catch (error) {
        setHealthStatus(prev => ({ ...prev, intelligenceLayer: 'unhealthy' }));
      }
    } else {
      setHealthStatus(prev => ({ ...prev, intelligenceLayer: 'healthy' }));
    }

    // RAG System Health Check
    try {
      await axios.get(`${RAG_BASE_URL}/api/rag/health`);
      setHealthStatus(prev => ({ ...prev, ragSystem: 'healthy' }));
    } catch (error) {
      setHealthStatus(prev => ({ ...prev, ragSystem: 'unhealthy' }));
    }
  };

  // Load documents
  const loadDocuments = useCallback(async (query = searchQuery) => {
    setLoading(true);
    try {
      // Always fetch all documents for all indices (no pagination)
      let size = 1000; // Fetch up to 1000 documents for all indices
      
      let endpoint = `${RAG_BASE_URL}/api/rag/${activeIndex}/all?size=${size}&from=0`;
      
      // Search handling - require minimum 3 characters for API search on all indices
      if (query && query.length >= 3) {
        endpoint = `${RAG_BASE_URL}/api/rag/${activeIndex}/search?query=${encodeURIComponent(query)}&size=${size}&from=0`;
      }
      // For queries less than 3 characters, we'll load all and filter client-side

      const response = await axios.get(endpoint);
      
      // Handle different response formats
      let documentsData = [];
      let total = 0;
      
      if (response.data) {
        // Check if response has documents array
        if (Array.isArray(response.data.documents)) {
          documentsData = response.data.documents;
          total = response.data.total || documentsData.length;
        } 
        // Check if response itself is an array
        else if (Array.isArray(response.data)) {
          documentsData = response.data;
          total = documentsData.length;
        }
        // Check if response has a different structure for search results
        else if (response.data.hits || response.data.results) {
          documentsData = response.data.hits || response.data.results || [];
          total = response.data.total || documentsData.length;
        }
        // Fallback to empty array
        else {
          console.warn('Unexpected response format:', response.data);
          documentsData = [];
          total = 0;
        }
      }
      
      // Client-side filtering for short queries
      if (query && documentsData.length > 0) {
        // For sectors_brands with short queries
        if (activeIndex === 'sectors_brands' && query.length < 3) {
          const lowerQuery = query.toLowerCase();
          documentsData = documentsData.filter(doc => {
            const brandName = getDocumentField(doc, 'brand_name')?.toLowerCase() || '';
            const categoryName = getDocumentField(doc, 'category_name')?.toLowerCase() || '';
            const subcategoryName = getDocumentField(doc, 'subcategory_name')?.toLowerCase() || '';
            return brandName.includes(lowerQuery) || 
                   categoryName.includes(lowerQuery) || 
                   subcategoryName.includes(lowerQuery);
          });
        }
        // Note: For other indices, we use API search, no client-side filtering needed
      }
      
      setDocuments(documentsData);
      setTotalDocs(total);
    } catch (error) {
      console.error('Error loading documents:', error);
      setAlert({ type: 'danger', message: 'Failed to load documents' });
      setDocuments([]); // Ensure documents is always an array
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  }, [activeIndex]);

  useEffect(() => {
    checkHealth();
    const healthInterval = setInterval(checkHealth, 60000); // Check every minute
    return () => clearInterval(healthInterval);
  }, []);

  useEffect(() => {
    loadDocuments(searchQuery);
  }, [activeIndex]); // Removed pagination deps since we're fetching all
  
  // Handle search execution
  const handleSearch = () => {
    setSearchQuery(searchInput);
    loadDocuments(searchInput);
  };
  
  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    loadDocuments('');
  };

  // Add document
  const handleAddDocument = async () => {
    try {
      // Format document data based on index type
      let documentData = newDocument;
      
      // For documents index, wrap the content in the expected structure
      if (activeIndex === 'documents') {
        documentData = {
          doc: newDocument.doc || newDocument.content || ''
        };
      }
      
      // For sectors_brands index, format as JSON string
      if (activeIndex === 'sectors_brands') {
        documentData = {
          type: newDocument.type || 'category',
          json_data: JSON.stringify(newDocument)
        };
      }
      
      await axios.post(`${RAG_BASE_URL}/api/rag/${activeIndex}/document`, documentData, {
        headers: { 'Content-Type': 'application/json' }
      });
      setShowAddModal(false);
      setNewDocument({});
      loadDocuments();
      setAlert({ type: 'success', message: 'Document added successfully' });
    } catch (error) {
      console.error('Error adding document:', error);
      setAlert({ type: 'danger', message: 'Failed to add document' });
    }
  };

  // Update document
  const handleUpdateDocument = async () => {
    try {
      // Format document data based on index type
      let documentData = editingDoc;
      
      // For documents index, ensure the content is in the expected structure
      if (activeIndex === 'documents') {
        documentData = {
          doc: editingDoc.doc || editingDoc.content || ''
        };
      }
      
      // For sectors_brands index, format as JSON string
      if (activeIndex === 'sectors_brands') {
        const { id, ...dataWithoutId } = editingDoc;
        documentData = {
          type: editingDoc.type || 'category',
          json_data: JSON.stringify(dataWithoutId)
        };
      }
      
      await axios.put(
        `${RAG_BASE_URL}/api/rag/${activeIndex}/document/${editingDoc.id}`,
        documentData,
        { headers: { 'Content-Type': 'application/json' } }
      );
      setShowEditModal(false);
      setEditingDoc(null);
      loadDocuments();
      setAlert({ type: 'success', message: 'Document updated successfully' });
    } catch (error) {
      console.error('Error updating document:', error);
      setAlert({ type: 'danger', message: 'Failed to update document' });
    }
  };

  // Delete documents
  const handleDeleteDocuments = async () => {
    try {
      if (selectedDocs.length === 1) {
        await axios.delete(`${RAG_BASE_URL}/api/rag/${activeIndex}/document/${selectedDocs[0]}`);
      } else {
        await axios.delete(`${RAG_BASE_URL}/api/rag/${activeIndex}/bulk`, {
          headers: { 'Content-Type': 'application/json' },
          data: selectedDocs
        });
      }
      setShowDeleteModal(false);
      setSelectedDocs([]);
      loadDocuments();
      setAlert({ type: 'success', message: 'Documents deleted successfully' });
    } catch (error) {
      setAlert({ type: 'danger', message: 'Failed to delete documents' });
    }
  };

  // Export data
  const handleExport = () => {
    const dataStr = JSON.stringify(documents || [], null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${activeIndex}_export_${new Date().toISOString()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Refresh index
  const handleRefresh = async () => {
    try {
      await axios.post(`${RAG_BASE_URL}/api/rag/${activeIndex}/refresh`);
      loadDocuments();
      setAlert({ type: 'success', message: 'Index refreshed successfully' });
    } catch (error) {
      setAlert({ type: 'danger', message: 'Failed to refresh index' });
    }
  };

  const getHealthIcon = (status) => {
    switch(status) {
      case 'healthy':
        return <CheckCircle className="text-success" size={20} />;
      case 'unhealthy':
        return <XCircle className="text-danger" size={20} />;
      default:
        return <Spinner animation="border" size="sm" />;
    }
  };

  const getFieldsForIndex = (index, docType = null) => {
    switch(index) {
      case 'documents':
        return ['doc'];  // Changed to match API response structure
      case 'question_sql':
        return ['question', 'sql'];
      case 'ddl':
        return ['table_name', 'ddl', 'database'];
      case 'sectors_brands':
        // For sectors_brands, show different fields based on document type
        if (docType === 'brand') {
          return ['type', 'brand_name', 'category_name', 'subcategory_name', 'description', 'country_code'];
        } else if (docType === 'category') {
          return ['type', 'category_name', 'subcategory_name'];
        }
        // Default when showing mixed types
        return ['type', 'brand_name', 'category_name', 'subcategory_name'];
      case 'fewshot':
        return ['question', 'answer', 'category'];
      default:
        return [];
    }
  };
  
  // Helper function to safely access nested document fields
  const getDocumentField = (doc, field) => {
    // For documents index, data is nested in source.doc
    if (activeIndex === 'documents' && field === 'doc') {
      return doc?.source?.doc || doc?.doc || '-';
    }
    
    // For sectors_brands index, use parsed_data if available, otherwise parse JSON
    if (activeIndex === 'sectors_brands') {
      // First check if parsed_data exists
      if (doc?.parsed_data && doc.parsed_data[field] !== undefined) {
        // Handle arrays like aliases
        if (Array.isArray(doc.parsed_data[field])) {
          return doc.parsed_data[field].join(', ') || '-';
        }
        return doc.parsed_data[field] || '-';
      }
      
      // Fallback to parsing JSON data
      if (doc?.source?.json_data) {
        try {
          const jsonData = JSON.parse(doc.source.json_data);
          if (Array.isArray(jsonData[field])) {
            return jsonData[field].join(', ') || '-';
          }
          return jsonData[field] || '-';
        } catch (e) {
          console.error('Error parsing JSON:', e);
        }
      }
      
      // Try direct field access
      return doc?.[field] || '-';
    }
    
    // For other indices, try source first, then direct field
    if (doc?.source && doc.source[field] !== undefined) {
      return doc.source[field];
    }
    
    return doc?.[field] || '-';
  };

  return (
    <div className="rag-management">
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col xs={12} md={8}>
            <div className="d-flex align-items-center">
              <Button 
                variant="outline-secondary" 
                className="me-3"
                onClick={() => navigate('/')}
              >
                <ArrowLeft size={18} className="me-2" />
                Back to Dashboard
              </Button>
              <h1 className="page-title mb-0">
                <Database className="me-2" />
                RAG Management
              </h1>
            </div>
          </Col>
        </Row>

        {/* Health Status Cards */}
        <Row className="mb-4">
          <Col md={4}>
            <Card className="health-card" bg="dark" text="light">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">CMO Backend</h6>
                  <small className="text-muted">cmo-app.cmoverse.ai</small>
                </div>
                {getHealthIcon(healthStatus.cmoBackend)}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="health-card" bg="dark" text="light">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Intelligence Layer</h6>
                  <small className="text-muted">cmo-chat-with-data-proxy</small>
                </div>
                {getHealthIcon(healthStatus.intelligenceLayer)}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="health-card" bg="dark" text="light">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">RAG System</h6>
                  <small className="text-muted">localhost:8505</small>
                </div>
                {getHealthIcon(healthStatus.ragSystem)}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Alert */}
        {alert && (
          <Alert 
            variant={alert.type} 
            dismissible 
            onClose={() => setAlert(null)}
            className="mb-4"
          >
            {alert.message}
          </Alert>
        )}

        {/* Main Content */}
        <Card className="main-card" bg="dark" text="light">
          <Card.Body>
            <Tab.Container activeKey={activeIndex} onSelect={(key) => {
              setActiveIndex(key);
              setSearchQuery(''); // Clear search when switching tabs
              setSearchInput(''); // Clear search input when switching tabs
              setSelectedDocs([]); // Clear selections when switching tabs
              setSectorsBrandsTab('all'); // Reset sub-tab when switching main tabs
            }}>
              {/* Actions Bar */}
              <Row className="mb-4">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search size={18} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder={activeIndex === 'sectors_brands' ? "Search (min 3 chars for API)..." : "Search documents..."}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                    />
                    {searchInput && (
                      <Button 
                        variant="outline-secondary"
                        onClick={handleClearSearch}
                        title="Clear search"
                        style={{ borderLeft: 'none' }}
                      >
                        <X size={18} />
                      </Button>
                    )}
                    <Button 
                      variant="primary" 
                      onClick={handleSearch}
                      disabled={activeIndex === 'sectors_brands' && searchInput.length > 0 && searchInput.length < 3}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Search
                    </Button>
                  </InputGroup>
                  {activeIndex === 'sectors_brands' && searchInput.length > 0 && searchInput.length < 3 && (
                    <small className="text-muted d-block mt-1">
                      Showing client-side filtered results. Enter at least 3 characters for server search.
                    </small>
                  )}
                </Col>
                <Col md={6} className="text-end">
                  <Button 
                    variant="success" 
                    className="me-2"
                    onClick={() => setShowAddModal(true)}
                  >
                    <Plus size={18} /> Add
                  </Button>
                  <Button 
                    variant="danger" 
                    className="me-2"
                    disabled={selectedDocs.length === 0}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <Trash2 size={18} /> Delete
                  </Button>
                  <Button 
                    variant="info" 
                    className="me-2"
                    onClick={handleExport}
                  >
                    <Download size={18} /> Export
                  </Button>
                  <Button 
                    variant="outline-secondary"
                    onClick={handleRefresh}
                  >
                    <RefreshCw size={18} /> Refresh
                  </Button>
                </Col>
              </Row>

              {/* Index Tabs */}
              <Nav variant="tabs" className="mb-4">
                {INDICES.map(index => (
                  <Nav.Item key={index.key}>
                    <Nav.Link eventKey={index.key} className="index-tab">
                      <index.icon size={18} className="me-2" />
                      {index.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              
              {/* Sub-tabs for Sectors & Brands */}
              {activeIndex === 'sectors_brands' && (
                <Nav variant="pills" className="mb-3">
                  <Nav.Item>
                    <Nav.Link 
                      active={sectorsBrandsTab === 'all'}
                      onClick={() => {
                        setSectorsBrandsTab('all');
                      }}
                      className="me-2"
                    >
                      All ({documents.length})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={sectorsBrandsTab === 'brands'}
                      onClick={() => {
                        setSectorsBrandsTab('brands');
                      }}
                      className="me-2"
                    >
                      Brands ({documents.filter(d => getDocumentField(d, 'type') === 'brand').length})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={sectorsBrandsTab === 'categories'}
                      onClick={() => {
                        setSectorsBrandsTab('categories');
                      }}
                    >
                      Categories ({documents.filter(d => getDocumentField(d, 'type') === 'category').length})
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              )}

              {/* Documents Table */}
              <Tab.Content>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <>
                    {(() => {
                      // Filter documents based on type for sectors_brands
                      let filteredDocs = documents || [];
                      if (activeIndex === 'sectors_brands' && sectorsBrandsTab !== 'all') {
                        const filterType = sectorsBrandsTab === 'brands' ? 'brand' : 'category';
                        filteredDocs = filteredDocs.filter(doc => 
                          getDocumentField(doc, 'type') === filterType
                        );
                      }
                      
                      return (
                        <>
                          <div style={{ 
                            overflowX: 'auto',
                            maxHeight: '600px',
                            overflowY: 'auto'
                          }}>
                            <Table className="documents-table" variant="dark" style={{ minWidth: '800px' }}>
                            <thead>
                              <tr>
                                <th style={{ width: '50px', textAlign: 'left' }}>
                                  <Form.Check
                                    checked={selectedDocs.length === filteredDocs.length && filteredDocs.length > 0}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedDocs(filteredDocs.map(d => d.id || d._id));
                                      } else {
                                        setSelectedDocs([]);
                                      }
                                    }}
                                  />
                                </th>
                                <th style={{ minWidth: '200px', textAlign: 'left' }}>ID</th>
                                {getFieldsForIndex(activeIndex, activeIndex === 'sectors_brands' && sectorsBrandsTab !== 'all' ? (sectorsBrandsTab === 'brands' ? 'brand' : 'category') : null).map(field => (
                            <th key={field} style={{ textAlign: 'left' }}>
                              {(() => {
                                if (activeIndex === 'documents' && field === 'doc') return 'Document Content';
                                if (activeIndex === 'sectors_brands') {
                                  if (field === 'type') return 'Type';
                                  if (field === 'brand_name') return 'Brand Name';
                                  if (field === 'category_name') return 'Category';
                                  if (field === 'subcategory_name') return 'Subcategory';
                                  if (field === 'description') return 'Description';
                                  if (field === 'country_code') return 'Country';
                                  if (field === 'aliases') return 'Aliases';
                                }
                                return field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ');
                              })()}
                            </th>
                          ))}
                          <th style={{ width: '80px', textAlign: 'left' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocs.map((doc) => (
                          <tr key={doc.id || doc._id}>
                            <td style={{ textAlign: 'left' }}>
                              <Form.Check
                                checked={selectedDocs.includes(doc.id || doc._id)}
                                onChange={(e) => {
                                  const docId = doc.id || doc._id;
                                  if (e.target.checked) {
                                    setSelectedDocs([...selectedDocs, docId]);
                                  } else {
                                    setSelectedDocs(selectedDocs.filter(id => id !== docId));
                                  }
                                }}
                              />
                            </td>
                            <td style={{ 
                              textAlign: 'left', 
                              fontSize: '0.85rem',
                              wordBreak: 'break-all',
                              minWidth: '200px'
                            }}>
                              {doc.id || doc._id || '-'}
                            </td>
                            {getFieldsForIndex(activeIndex, activeIndex === 'sectors_brands' && sectorsBrandsTab !== 'all' ? (sectorsBrandsTab === 'brands' ? 'brand' : 'category') : null).map(field => (
                              <td key={field} style={{ textAlign: 'left' }}>
                                <div style={{ 
                                  wordBreak: 'break-word', 
                                  whiteSpace: 'pre-wrap',
                                  maxWidth: field === 'description' ? '400px' : '300px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: field === 'description' ? 2 : 3,
                                  WebkitBoxOrient: 'vertical',
                                  color: field === 'type' 
                                    ? (getDocumentField(doc, field) === 'brand' ? '#58a6ff' : '#f0883e')
                                    : 'inherit'
                                }}>
                                  {field === 'type' && getDocumentField(doc, field) ? (
                                    <span className={`badge bg-${getDocumentField(doc, field) === 'brand' ? 'success' : 'info'}`}>
                                      {getDocumentField(doc, field).toUpperCase()}
                                    </span>
                                  ) : (
                                    getDocumentField(doc, field)
                                  )}
                                </div>
                              </td>
                            ))}
                            <td style={{ textAlign: 'left' }}>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => {
                                  // Extract the actual document data for editing
                                  let docToEdit = {
                                    id: doc.id || doc._id,
                                    ...doc.source,
                                    ...doc
                                  };
                                  
                                  // For sectors_brands, use parsed_data or parse the JSON data
                                  if (activeIndex === 'sectors_brands') {
                                    if (doc?.parsed_data) {
                                      docToEdit = {
                                        id: doc.id || doc._id,
                                        ...doc.parsed_data
                                      };
                                    } else if (doc?.source?.json_data) {
                                      try {
                                        const jsonData = JSON.parse(doc.source.json_data);
                                        docToEdit = {
                                          id: doc.id || doc._id,
                                          ...jsonData
                                        };
                                      } catch (e) {
                                        console.error('Error parsing JSON for edit:', e);
                                      }
                                    }
                                  }
                                  
                                  setEditingDoc(docToEdit);
                                  setShowEditModal(true);
                                }}
                              >
                                <Edit2 size={16} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    </div>

                    {/* Document count display */}
                    <div className="text-muted mt-4">
                      {activeIndex === 'sectors_brands' ? (
                        <>
                          Showing {filteredDocs.length} {sectorsBrandsTab === 'all' ? 'documents' : sectorsBrandsTab}
                          {sectorsBrandsTab === 'all' && 
                            ` (${documents.filter(d => getDocumentField(d, 'type') === 'brand').length} brands, ${documents.filter(d => getDocumentField(d, 'type') === 'category').length} categories)`}
                        </>
                      ) : (
                        <>Showing {filteredDocs.length} documents</>
                      )}
                      {searchQuery && ` (filtered by: "${searchQuery}")`}
                    </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </Tab.Content>
            </Tab.Container>
          </Card.Body>
        </Card>

        {/* Add Modal */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg" className="rag-modal" contentClassName="bg-dark text-light">
          <Modal.Header closeButton>
            <Modal.Title>Add New Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {getFieldsForIndex(activeIndex).map(field => (
              <Form.Group key={field} className="mb-3">
                <Form.Label>
                  {(() => {
                    if (activeIndex === 'documents' && field === 'doc') return 'Document Content';
                    if (activeIndex === 'sectors_brands') {
                      if (field === 'type') return 'Type';
                      if (field === 'category_name') return 'Category Name';
                      if (field === 'subcategory_name') return 'Subcategory Name';
                    }
                    return field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ');
                  })()}
                </Form.Label>
                <Form.Control
                  as={field.includes('content') || field.includes('ddl') || field.includes('sql') || field.includes('answer') || field === 'doc' ? 'textarea' : 'input'}
                  rows={field.includes('content') || field.includes('ddl') || field === 'doc' ? 5 : 3}
                  value={newDocument[field] || ''}
                  onChange={(e) => setNewDocument({...newDocument, [field]: e.target.value})}
                  placeholder={(() => {
                    if (activeIndex === 'documents' && field === 'doc') return 'Enter the document content...';
                    if (activeIndex === 'sectors_brands') {
                      if (field === 'type') return 'e.g., category, brand';
                      if (field === 'category_name') return 'e.g., Retail, Finance';
                      if (field === 'subcategory_name') return 'e.g., E-commerce, Banking';
                    }
                    return '';
                  })()}
                />
              </Form.Group>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              <X size={18} /> Cancel
            </Button>
            <Button variant="success" onClick={handleAddDocument}>
              <Save size={18} /> Add Document
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" className="rag-modal" contentClassName="bg-dark text-light">
          <Modal.Header closeButton>
            <Modal.Title>Edit Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingDoc && getFieldsForIndex(activeIndex).map(field => (
              <Form.Group key={field} className="mb-3">
                <Form.Label>
                  {(() => {
                    if (activeIndex === 'documents' && field === 'doc') return 'Document Content';
                    if (activeIndex === 'sectors_brands') {
                      if (field === 'type') return 'Type';
                      if (field === 'category_name') return 'Category Name';
                      if (field === 'subcategory_name') return 'Subcategory Name';
                    }
                    return field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ');
                  })()}
                </Form.Label>
                <Form.Control
                  as={field.includes('content') || field.includes('ddl') || field.includes('sql') || field.includes('answer') || field === 'doc' ? 'textarea' : 'input'}
                  rows={field.includes('content') || field.includes('ddl') || field === 'doc' ? 5 : 3}
                  value={editingDoc[field] || ''}
                  onChange={(e) => setEditingDoc({...editingDoc, [field]: e.target.value})}
                />
              </Form.Group>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              <X size={18} /> Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateDocument}>
              <Save size={18} /> Update Document
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} className="rag-modal" contentClassName="bg-dark text-light">
          <Modal.Header closeButton>
            <Modal.Title>
              <AlertCircle className="text-warning me-2" />
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete {selectedDocs.length} document(s)?</p>
            <Alert variant="danger">
              <strong>Warning:</strong> This action cannot be undone.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteDocuments}>
              Delete {selectedDocs.length} Document(s)
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default RAGManagement;